import { Action } from "redux";
import { IEventList } from "../event/eventList";
import { IEventListItem } from "../event/eventListItem";
import { IEventEditItem } from "../event/eventEditItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IEventEdit } from "../event/eventEdit";
import { IEventCompany } from "../event/eventCompany";
import { IFrontPage } from "../front/frontPage";
import { IMessageListItem } from "../message/messageListItem";
import { IMessageEditItem } from "../message/messageEditItem";
import { IMessageList } from "../message/messageList";
import { IMessageEdit } from "../message/messageEdit";
import { IIdCodeNameLogo } from "../common/idCodeNameLogo";
import { IDocumentList } from "../document/documentList";
import { IDocumentListItem, IDocumentListParentItem } from "../document/documentListItem";
import { ICompanyDocuments } from "../document/companyDocuments";
import { IDocumentCompanyListItem } from "../document/documentCompanyListItem";
import { ITaskListItem } from "../task/taskListItem";
import { ITaskList } from "../task/taskList";
import { ITaskEdit } from "../task/taskEdit";
import { IUnitItem } from "../unit/unitItem";
import { ITaskEditItem } from "../task/taskEditItem";
import { ITaskStateItem } from "../task/taskStateItem";
import { ITaskEditPriorityItem } from "../task/taskEditPriorityItem";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IEventTypeNode } from "../event/eventTypeNode";
import { IRecordListItemUserFurtherEducation } from "ui/Scripts/source/models/recordUserFurtherEducation/recordListItemUserFurtherEducation";
import { IUserFurtherEducationList } from "../userFurtherEducation/userFurtherEducationList";
import { IUserFurtherEducationEditItem } from "../userFurtherEducation/userFurtherEducationEditItem";
import { IUserFurtherEducationEdit } from "../userFurtherEducation/userFurtherEducationEdit";
import { InstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { IInstantMessageChannelItems } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItems";
import { IInstantMessageChannelEdit } from "ui/Scripts/source/models/instantMessage/instantMessageChannelEdit";
import { IDocumentViewItem } from "../document/documentViewItem";
import { IDocumentView } from "../document/documentView";
import { IRecordListItemUserQualification } from "ui/Scripts/source/models/recordUserQualification/recordListItemUserQualification";
import { IUserIdCardItem } from "../userIdCard/userIdCardItem";
import { IFrontPageInstantMessage } from "../front/frontPageInstantMessage";
import { IFrontPageMessage } from "../front/frontPageMessage";
import { IFrontPageEvent } from "../front/frontPageEvent";
import { IFrontPageTask } from "../front/frontPageTask";
import { IFrontPageDocument } from "../front/frontPageDocument";
import { IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IEventSettingSimple } from "ui/Scripts/source/models/eventSettings/eventSettingSimple";
import { IRecordFurtherEducationGroupNode } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNode";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { IFrontPageCalendarTask } from "../front/frontPageCalendarTask";
import { ICalendarTaskEdit } from "ui/Scripts/source/models/calendarTask/calendarTaskEdit";
import { IMobileCalendarTaskItems } from "../calendarTask/mobileCalendarTaskItems";

export interface IUser {
    name: string;
    age: number;
}

export interface ILoadingState {
    users: boolean;
}

export interface IBeforeInstallPromptEvent extends Event {
    //Returns an array of DOMString items containing the platforms on which the event was dispatched.
    //This is provided for user agents that want to present a choice of versions to the user such as,
    //for example, "web" or "play" which would allow the user to chose between a web version or
    //an Android version.
    readonly platforms: Array<string>;
    //Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
    readonly userChoice: Promise<{
        outcome: "accepted" | "dismissed",
        platform: string;
    }>;
    //Allows a developer to show the install prompt at a time of their own choosing.
    //This method returns a Promise.
    prompt(): Promise<void>;
}

export interface IInstallState {
    proposalEvent: IBeforeInstallPromptEvent;
}

/* eslint-disable no-unused-vars */
export enum ConfirmationDialogType {
    Warning,
    Information
}
/* eslint-enable no-unused-vars */

export interface IConfirmationCallback {
    (): void;
}

export interface IUpdateState {
    show: boolean;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
}

export interface IAlertState {
    message: string;
    info: string;
    error: string;
}

export interface IConfirmationState {
    type: ConfirmationDialogType;
    title: string;
    text: string;
    show: boolean;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
    onCancel: IConfirmationCallback;
}

export interface IUserState {
    initialized: boolean;
    companyId: string;
    companyName: string;
    userCode: string;
    userName: string;
    userPicture: string;
    authenticated: boolean;
    canUseMessages: boolean;
    canUseDocuments: boolean;
    canUseEvents: boolean;
    canUseTasks: boolean;
    canUseInstantMessages: boolean;
    canEditOwnRecord: boolean;
    canUseCalendarTasks: boolean;
    canUseMultipleCompanies: boolean;
}

export interface IFrontPageGeneralState {
    isLoading: boolean;
}

export interface IFrontPageMessageState {
    isLoading: boolean;
    messages: IMessageListItem[];
}

export interface IFrontPageEventState {
    isLoading: boolean;
    events: IEventListItem[];
}

export interface IFrontPageTaskState {
    isLoading: boolean;
    canCreateTasks: boolean;
    tasks: ITaskListItem[];
}

export interface IFrontPageDocumentState {
    isLoading: boolean;
    documents: ICompanyDocuments[];
}

export interface IFrontPageInstantMessageState {
    isLoading: boolean;
    channels: InstantMessageChannelItem[];
}

export interface IFrontPageCalendarTaskState {
    isLoading: boolean;
    calendarTasks: ICalendarTaskItem[];
}

export interface IFrontState {
    general: IFrontPageGeneralState;
    message: IFrontPageMessageState;
    event: IFrontPageEventState;
    task: IFrontPageTaskState;
    document: IFrontPageDocumentState;
    instantMessage: IFrontPageInstantMessageState;
    calendarTask: IFrontPageCalendarTaskState;
}

//Message
export interface IMessageListState {
    isLoading: boolean;
    hasMore: boolean;
    page: number;
    sortColumn: string;
    startDate: number;
    endDate: number;
    companyId: string;
    companies: IIdCodeNameLogo[];
    items: IMessageListItem[];
    lastOpenedMessageId: string;
}

export interface IMessageEditState {
    isLoading: boolean;
    item: IMessageEditItem;
    attachments: IFileAttachment[];
    returnUrl: string;
}

export interface IMessageState {
    messageList: IMessageListState;
    messageEdit: IMessageEditState;
}

//Event
export interface IEventListState {
    isLoading: boolean;
    hasMore: boolean;
    page: number;
    sortColumn: string;
    state: number;
    startDate: number;
    endDate: number;
    unitId: string;
    units: IUnitItem[];
    items: IEventListItem[];
    lastOpenedEventId: string;
}

export interface IEventEditState {
    isLoading: boolean;
    item: IEventEditItem;
    eventTypeNodes: IEventTypeNode[];
    eventTypeBNodes: IEventTypeNode[];
    eventTypeCNodes: IEventTypeNode[];
    attachments: IFileAttachment[];
    createCompany: IEventCompany;
    companies: IEventCompany[];
    units: IUnitItem[];
    urgencyClasses: IIdColorTitle[];
    eventSetting: IEventSettingSimple;
    userCompanyId: string;
    userCode: string;
    userName: string;
    userPicture: string;
    canHandleEvent: boolean;
    canRemoveEvent: boolean;
    canSaveEvent: boolean;
    canEditIdentificationData: boolean;
    userIsIsSystemAdmin: boolean;
    returnUrl: string;
}

export interface IEventState {
    eventList: IEventListState;
    eventEdit: IEventEditState;
}

//Task
export interface ITaskListState {
    isLoading: boolean;
    hasMore: boolean;
    page: number;
    sortColumn: string;
    stateId: string;
    unitId: string;
    units: IUnitItem[];
    taskStates: ITaskStateItem[];
    items: ITaskListItem[];
    lastOpenedTaskId: string;
    canCreateTasks: boolean;
}

export interface ITaskEditState {
    isLoading: boolean;
    item: ITaskEditItem;
    taskTypes: ITaskEditTypeItem[];
    taskStates: ITaskStateItem[];
    taskPriorities: ITaskEditPriorityItem[];
    units: IUnitItem[];
    userCode: string;
    userName: string;
    userPicture: string;
    canHandleTask: boolean;
    canRemoveTask: boolean;
    canChangeTaskState: boolean;
    userIsIsSystemAdmin: boolean;
    returnUrl: string;
}

export interface ITaskState {
    taskList: ITaskListState;
    taskEdit: ITaskEditState;
}

//Document
export interface IDocumentListState {
    isLoading: boolean;
    hasMore: boolean;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    companyId: string;
    parent: IDocumentListParentItem;
    items: IDocumentListItem[];
    favoriteIds: string[];
    companies: IDocumentCompanyListItem[];
    lastOpenedDocumentId: string;
}

export interface IDocumentViewState {
    isLoading: boolean;
    item: IDocumentViewItem;
    returnUrl: string;
}

export interface IDocumentState {
    documentList: IDocumentListState;
    documentView: IDocumentViewState;
}

export interface IUserFurtherEducationListState {
    isLoading: boolean;
    hasMore: boolean;
    page: number;
    archived: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IRecordListItemUserFurtherEducation[];
    qualifications: IRecordListItemUserQualification[];
    lastOpenedUserFurtherEducationId: string;
}

export interface IUserFurtherEducationEditState {
    isLoading: boolean;
    item: IUserFurtherEducationEditItem;
    attachments: IFileAttachment[];
    furtherEducationGroups: IRecordFurtherEducationGroupNode[];
    qualifications: IRecordListItemUserQualification[];
    returnUrl: string;
}

export interface IUserFurtherEducationState {
    userFurtherEducationList: IUserFurtherEducationListState;
    userFurtherEducationEdit: IUserFurtherEducationEditState;
}

//InstantMessage
export interface IInstantMessageChannelListState {
    isLoading: boolean;
    items: InstantMessageChannelItem[];
    lastOpenedInstantMessageChannelId: string;
}

export interface IInstantMessageChannelEditState {
    isLoading: boolean;
    item: IInstantMessageChannelEdit;
    returnUrl: string;
}

export interface IInstantMessageChannelState {
    returnUrl: string;
}

export interface IInstantMessageState {
    instantMessageChannelList: IInstantMessageChannelListState;
    instantMessageChannelEdit: IInstantMessageChannelEditState;
    instantMessageChannel: IInstantMessageChannelState;
}

//CalendarTask
export interface ICalendarTaskViewState {
    isLoading: boolean;
    filter: string;
    stateId: string;
    categoryId: string;
    items: IMobileCalendarTaskItems;
    lastOpenedCalendarTasklId: string;
}

export interface ICalendarTaskEditState {
    isLoading: boolean;
    item: ICalendarTaskEdit;
    returnUrl: string;
}

export interface ICalendarTaskState {
    calendarTaskView: ICalendarTaskViewState;
    calendarTaskEdit: ICalendarTaskEditState;
}

//IdCard
export interface IUserIdCardState {
    isLoading: boolean;
    idCardItem: IUserIdCardItem;
}

export interface IApplicationState {
    //Fetch
    fetchCount: number;
    //Install
    install: IInstallState;
    //Update
    update: IUpdateState;
    //Alert
    alert: IAlertState;
    //Confirmation
    confirmation: IConfirmationState;
    //User
    user: IUserState;
    //Front
    front: IFrontState;
    //Message
    message: IMessageState;
    //Event
    event: IEventState;
    //Task
    task: ITaskState;
    //Document
    document: IDocumentState;
    //UserFurtherEducation
    userFurtherEducation: IUserFurtherEducationState;
    //InstantMessage
    instantMessage: IInstantMessageState;
    //CalendarTask
    calendarTask: ICalendarTaskState
    //IdCard
    idCard: IUserIdCardState;
}

//Action type constants

//Install
export const INSTALL_SET = "INSTALL_SET";
export const INSTALL_CLEAR = "INSTALL_CLEAR";

export type T_INSTALL_SET = typeof INSTALL_SET;
export type T_INSTALL_CLEAR = typeof INSTALL_CLEAR;

//Update
export const UPDATE_SET = "UPDATE_SET";
export const UPDATE_CLEAR = "UPDATE_CLEAR";

export type T_UPDATE_SET = typeof UPDATE_SET;
export type T_UPDATE_CLEAR = typeof UPDATE_CLEAR;

//Alert
export const ALERT_SUCCESS = "ALERT_SUCCESS";
export const ALERT_INFO = "ALERT_INFO";
export const ALERT_ERROR = "ALERT_ERROR";
export const ALERT_CLEAR = "ALERT_CLEAR";
export const ALERT_SUCCESS_CLEAR = "ALERT_SUCCESS_CLEAR";
export const ALERT_INFO_CLEAR = "ALERT_INFO_CLEAR";

export type T_ALERT_SUCCESS = typeof ALERT_SUCCESS;
export type T_ALERT_INFO = typeof ALERT_INFO;
export type T_ALERT_ERROR = typeof ALERT_ERROR;
export type T_ALERT_CLEAR = typeof ALERT_CLEAR;
export type T_ALERT_SUCCESS_CLEAR = typeof ALERT_SUCCESS_CLEAR;
export type T_ALERT_INFO_CLEAR = typeof ALERT_INFO_CLEAR;

//Confirmation Dialog
export const CONFIRMATION_SET = "CONFIRMATION_SET";
export const CONFIRMATION_CLEAR = "CONFIRMATION_CLEAR";

export type T_CONFIRMATION_SET = typeof CONFIRMATION_SET;
export type T_CONFIRMATION_CLEAR = typeof CONFIRMATION_CLEAR;

//Fetch
export const FETCH_START = "FETCH_START";
export const FETCH_END = "FETCH_END";

export type T_FETCH_START = typeof FETCH_START;
export type T_FETCH_END = typeof FETCH_END;

//Token
export const LOGIN_SUCCESS = "LOGIN_SUCCESS";
export const LOGIN_FAILURE = "LOGIN_FAILURE";

export const LOGOUT_SUCCESS = "LOGOUT_SUCCESS";

export type T_LOGIN_SUCCESS = typeof LOGIN_SUCCESS;
export type T_LOGIN_FAILURE = typeof LOGIN_FAILURE;

export type T_LOGOUT_SUCCESS = typeof LOGOUT_SUCCESS;

//FrontPage
export const FRONT_PAGE_REQUEST = "FRONT_PAGE_REQUEST";
export const FRONT_PAGE_SUCCESS = "FRONT_PAGE_SUCCESS";
export const FRONT_PAGE_FAILURE = "FRONT_PAGE_FAILURE";

export type T_FRONT_PAGE_REQUEST = typeof FRONT_PAGE_REQUEST;
export type T_FRONT_PAGE_SUCCESS = typeof FRONT_PAGE_SUCCESS;
export type T_FRONT_PAGE_FAILURE = typeof FRONT_PAGE_FAILURE;

//FrontPage: Message
export const FRONT_PAGE_MESSAGE_REQUEST = "FRONT_PAGE_MESSAGE_REQUEST";
export const FRONT_PAGE_MESSAGE_SUCCESS = "FRONT_PAGE_MESSAGE_SUCCESS";
export const FRONT_PAGE_MESSAGE_FAILURE = "FRONT_PAGE_MESSAGE_FAILURE";

export type T_FRONT_PAGE_MESSAGE_REQUEST = typeof FRONT_PAGE_MESSAGE_REQUEST;
export type T_FRONT_PAGE_MESSAGE_SUCCESS = typeof FRONT_PAGE_MESSAGE_SUCCESS;
export type T_FRONT_PAGE_MESSAGE_FAILURE = typeof FRONT_PAGE_MESSAGE_FAILURE;

//FrontPage: Event
export const FRONT_PAGE_EVENT_REQUEST = "FRONT_PAGE_EVENT_REQUEST";
export const FRONT_PAGE_EVENT_SUCCESS = "FRONT_PAGE_EVENT_SUCCESS";
export const FRONT_PAGE_EVENT_FAILURE = "FRONT_PAGE_EVENT_FAILURE";

export type T_FRONT_PAGE_EVENT_REQUEST = typeof FRONT_PAGE_EVENT_REQUEST;
export type T_FRONT_PAGE_EVENT_SUCCESS = typeof FRONT_PAGE_EVENT_SUCCESS;
export type T_FRONT_PAGE_EVENT_FAILURE = typeof FRONT_PAGE_EVENT_FAILURE;

//FrontPage: Task
export const FRONT_PAGE_TASK_REQUEST = "FRONT_PAGE_TASK_REQUEST";
export const FRONT_PAGE_TASK_SUCCESS = "FRONT_PAGE_TASK_SUCCESS";
export const FRONT_PAGE_TASK_FAILURE = "FRONT_PAGE_TASK_FAILURE";

export type T_FRONT_PAGE_TASK_REQUEST = typeof FRONT_PAGE_TASK_REQUEST;
export type T_FRONT_PAGE_TASK_SUCCESS = typeof FRONT_PAGE_TASK_SUCCESS;
export type T_FRONT_PAGE_TASK_FAILURE = typeof FRONT_PAGE_TASK_FAILURE;

//FrontPage: Document
export const FRONT_PAGE_DOCUMENT_REQUEST = "FRONT_PAGE_DOCUMENT_REQUEST";
export const FRONT_PAGE_DOCUMENT_SUCCESS = "FRONT_PAGE_DOCUMENT_SUCCESS";
export const FRONT_PAGE_DOCUMENT_FAILURE = "FRONT_PAGE_DOCUMENT_FAILURE";

export type T_FRONT_PAGE_DOCUMENT_REQUEST = typeof FRONT_PAGE_DOCUMENT_REQUEST;
export type T_FRONT_PAGE_DOCUMENT_SUCCESS = typeof FRONT_PAGE_DOCUMENT_SUCCESS;
export type T_FRONT_PAGE_DOCUMENT_FAILURE = typeof FRONT_PAGE_DOCUMENT_FAILURE;

//FrontPage: InstantMessage
export const FRONT_PAGE_INSTANTMESSAGE_REQUEST = "FRONT_PAGE_INSTANTMESSAGE_REQUEST";
export const FRONT_PAGE_INSTANTMESSAGE_SUCCESS = "FRONT_PAGE_INSTANTMESSAGE_SUCCESS";
export const FRONT_PAGE_INSTANTMESSAGE_FAILURE = "FRONT_PAGE_INSTANTMESSAGE_FAILURE";

export type T_FRONT_PAGE_INSTANTMESSAGE_REQUEST = typeof FRONT_PAGE_INSTANTMESSAGE_REQUEST;
export type T_FRONT_PAGE_INSTANTMESSAGE_SUCCESS = typeof FRONT_PAGE_INSTANTMESSAGE_SUCCESS;
export type T_FRONT_PAGE_INSTANTMESSAGE_FAILURE = typeof FRONT_PAGE_INSTANTMESSAGE_FAILURE;

//FrontPage: CalendarTask
export const FRONT_PAGE_CALENDARTASK_REQUEST = "FRONT_PAGE_CALENDARTASK_REQUEST";
export const FRONT_PAGE_CALENDARTASK_SUCCESS = "FRONT_PAGE_CALENDARTASK_SUCCESS";
export const FRONT_PAGE_CALENDARTASK_FAILURE = "FRONT_PAGE_CALENDARTASK_FAILURE";

export type T_FRONT_PAGE_CALENDARTASK_REQUEST = typeof FRONT_PAGE_CALENDARTASK_REQUEST;
export type T_FRONT_PAGE_CALENDARTASK_SUCCESS = typeof FRONT_PAGE_CALENDARTASK_SUCCESS;
export type T_FRONT_PAGE_CALENDARTASK_FAILURE = typeof FRONT_PAGE_CALENDARTASK_FAILURE;

//Message
export const MESSAGE_LIST_REQUEST = "MESSAGE_LIST_REQUEST";
export const MESSAGE_LIST_SUCCESS = "MESSAGE_LIST_SUCCESS";
export const MESSAGE_LIST_REFRESH = "MESSAGE_LIST_REFRESH";
export const MESSAGE_LIST_FAILURE = "MESSAGE_LIST_FAILURE";
export const MESSAGE_LIST_SETLASTOPENED = "MESSAGE_LIST_SETLASTOPENED";
export const MESSAGE_LIST_RESET = "MESSAGE_LIST_RESET";

export const MESSAGE_EDIT_REQUEST = "MESSAGE_EDIT_REQUEST";
export const MESSAGE_EDIT_SUCCESS = "MESSAGE_EDIT_SUCCESS";
export const MESSAGE_EDIT_FAILURE = "MESSAGE_EDIT_FAILURE";
export const MESSAGE_EDIT_RESET = "MESSAGE_EDIT_RESET";
export const MESSAGE_EDIT_SETRETURNURL = "MESSAGE_EDIT_SETRETURNURL";

export type T_MESSAGE_LIST_REQUEST = typeof MESSAGE_LIST_REQUEST;
export type T_MESSAGE_LIST_SUCCESS = typeof MESSAGE_LIST_SUCCESS;
export type T_MESSAGE_LIST_REFRESH = typeof MESSAGE_LIST_REFRESH;
export type T_MESSAGE_LIST_FAILURE = typeof MESSAGE_LIST_FAILURE;
export type T_MESSAGE_LIST_SETLASTOPENED = typeof MESSAGE_LIST_SETLASTOPENED;
export type T_MESSAGE_LIST_RESET = typeof MESSAGE_LIST_RESET;
export type T_MESSAGE_EDIT_SETRETURNURL = typeof MESSAGE_EDIT_SETRETURNURL;

export type T_MESSAGE_EDIT_REQUEST = typeof MESSAGE_EDIT_REQUEST;
export type T_MESSAGE_EDIT_SUCCESS = typeof MESSAGE_EDIT_SUCCESS;
export type T_MESSAGE_EDIT_FAILURE = typeof MESSAGE_EDIT_FAILURE;
export type T_MESSAGE_EDIT_RESET = typeof MESSAGE_EDIT_RESET;

//Event
export const EVENT_LIST_REQUEST = "EVENT_LIST_REQUEST";
export const EVENT_LIST_SUCCESS = "EVENT_LIST_SUCCESS";
export const EVENT_LIST_REFRESH = "EVENT_LIST_REFRESH";
export const EVENT_LIST_FAILURE = "EVENT_LIST_FAILURE";
export const EVENT_LIST_REMOVE = "EVENT_LIST_REMOVE";
export const EVENT_LIST_RESET = "EVENT_LIST_RESET";
export const EVENT_LIST_SETLASTOPENED = "EVENT_LIST_SETLASTOPENED";
export const EVENT_LIST_SETLOCKED = "EVENT_LIST_SETLOCKED";

export const EVENT_EDIT_REQUEST = "EVENT_EDIT_REQUEST";
export const EVENT_EDIT_SUCCESS = "EVENT_EDIT_SUCCESS";
export const EVENT_EDIT_FAILURE = "EVENT_EDIT_FAILURE";
export const EVENT_EDIT_RESET = "EVENT_EDIT_RESET";
export const EVENT_EDIT_SETRETURNURL = "EVENT_EDIT_SETRETURNURL";

export type T_EVENT_LIST_REQUEST = typeof EVENT_LIST_REQUEST;
export type T_EVENT_LIST_SUCCESS = typeof EVENT_LIST_SUCCESS;
export type T_EVENT_LIST_REFRESH = typeof EVENT_LIST_REFRESH;
export type T_EVENT_LIST_FAILURE = typeof EVENT_LIST_FAILURE;
export type T_EVENT_LIST_REMOVE = typeof EVENT_LIST_REMOVE;
export type T_EVENT_LIST_RESET = typeof EVENT_LIST_RESET;
export type T_EVENT_LIST_SETLASTOPENED = typeof EVENT_LIST_SETLASTOPENED;
export type T_EVENT_LIST_SETLOCKED = typeof EVENT_LIST_SETLOCKED;

export type T_EVENT_EDIT_REQUEST = typeof EVENT_EDIT_REQUEST;
export type T_EVENT_EDIT_SUCCESS = typeof EVENT_EDIT_SUCCESS;
export type T_EVENT_EDIT_FAILURE = typeof EVENT_EDIT_FAILURE;
export type T_EVENT_EDIT_RESET = typeof EVENT_EDIT_RESET;
export type T_EVENT_EDIT_SETRETURNURL = typeof EVENT_EDIT_SETRETURNURL;

//Task
export const TASK_LIST_REQUEST = "TASK_LIST_REQUEST";
export const TASK_LIST_SUCCESS = "TASK_LIST_SUCCESS";
export const TASK_LIST_REFRESH = "TASK_LIST_REFRESH";
export const TASK_LIST_FAILURE = "TASK_LIST_FAILURE";
export const TASK_LIST_REMOVE = "TASK_LIST_REMOVE";
export const TASK_LIST_RESET = "TASK_LIST_RESET";
export const TASK_LIST_SETLASTOPENED = "TASK_LIST_SETLASTOPENED";
export const TASK_LIST_SETLOCKED = "TASK_LIST_SETLOCKED";

export const TASK_EDIT_REQUEST = "TASK_EDIT_REQUEST";
export const TASK_EDIT_SUCCESS = "TASK_EDIT_SUCCESS";
export const TASK_EDIT_FAILURE = "TASK_EDIT_FAILURE";
export const TASK_EDIT_RESET = "TASK_EDIT_RESET";
export const TASK_EDIT_SETRETURNURL = "TASK_EDIT_SETRETURNURL";

export type T_TASK_LIST_REQUEST = typeof TASK_LIST_REQUEST;
export type T_TASK_LIST_SUCCESS = typeof TASK_LIST_SUCCESS;
export type T_TASK_LIST_REFRESH = typeof TASK_LIST_REFRESH;
export type T_TASK_LIST_FAILURE = typeof TASK_LIST_FAILURE;
export type T_TASK_LIST_REMOVE = typeof TASK_LIST_REMOVE;
export type T_TASK_LIST_RESET = typeof TASK_LIST_RESET;
export type T_TASK_LIST_SETLASTOPENED = typeof TASK_LIST_SETLASTOPENED;
export type T_TASK_LIST_SETLOCKED = typeof TASK_LIST_SETLOCKED;

export type T_TASK_EDIT_REQUEST = typeof TASK_EDIT_REQUEST;
export type T_TASK_EDIT_SUCCESS = typeof TASK_EDIT_SUCCESS;
export type T_TASK_EDIT_FAILURE = typeof TASK_EDIT_FAILURE;
export type T_TASK_EDIT_RESET = typeof TASK_EDIT_RESET;
export type T_TASK_EDIT_SETRETURNURL = typeof TASK_EDIT_SETRETURNURL;

//Document
export const DOCUMENT_LIST_REQUEST = "DOCUMENT_LIST_REQUEST";
export const DOCUMENT_LIST_SUCCESS = "DOCUMENT_LIST_SUCCESS";
export const DOCUMENT_LIST_FAILURE = "DOCUMENT_LIST_FAILURE";
export const DOCUMENT_LIST_SETLASTOPENED = "DOCUMENT_LIST_SETLASTOPENED";
export const DOCUMENT_LIST_RESET = "DOCUMENT_LIST_RESET";
export const DOCUMENT_LIST_ADD_FAVORITE = "DOCUMENT_LIST_ADD_FAVORITE";
export const DOCUMENT_LIST_REMOVE_FAVORITE = "DOCUMENT_LIST_REMOVE_FAVORITE";

export const DOCUMENT_VIEW_REQUEST = "DOCUMENT_VIEW_REQUEST";
export const DOCUMENT_VIEW_SUCCESS = "DOCUMENT_VIEW_SUCCESS";
export const DOCUMENT_VIEW_FAILURE = "DOCUMENT_VIEW_FAILURE";
export const DOCUMENT_VIEW_RESET = "DOCUMENT_VIEW_RESET";
export const DOCUMENT_VIEW_SETRETURNURL = "DOCUMENT_VIEW_SETRETURNURL";

export type T_DOCUMENT_LIST_REQUEST = typeof DOCUMENT_LIST_REQUEST;
export type T_DOCUMENT_LIST_SUCCESS = typeof DOCUMENT_LIST_SUCCESS;
export type T_DOCUMENT_LIST_FAILURE = typeof DOCUMENT_LIST_FAILURE;
export type T_DOCUMENT_LIST_SETLASTOPENED = typeof DOCUMENT_LIST_SETLASTOPENED;
export type T_DOCUMENT_LIST_RESET = typeof DOCUMENT_LIST_RESET;
export type T_DOCUMENT_LIST_ADD_FAVORITE = typeof DOCUMENT_LIST_ADD_FAVORITE;
export type T_DOCUMENT_LIST_REMOVE_FAVORITE = typeof DOCUMENT_LIST_REMOVE_FAVORITE;

export type T_DOCUMENT_VIEW_REQUEST = typeof DOCUMENT_VIEW_REQUEST;
export type T_DOCUMENT_VIEW_SUCCESS = typeof DOCUMENT_VIEW_SUCCESS;
export type T_DOCUMENT_VIEW_FAILURE = typeof DOCUMENT_VIEW_FAILURE;
export type T_DOCUMENT_VIEW_RESET = typeof DOCUMENT_VIEW_RESET;
export type T_DOCUMENT_VIEW_SETRETURNURL = typeof DOCUMENT_VIEW_SETRETURNURL;

//UserFurtherEducation
export const USERFURTHEREDUCATION_LIST_REQUEST = "USERFURTHEREDUCATION_LIST_REQUEST";
export const USERFURTHEREDUCATION_LIST_SUCCESS = "USERFURTHEREDUCATION_LIST_SUCCESS";
export const USERFURTHEREDUCATION_LIST_REFRESH = "USERFURTHEREDUCATION_LIST_REFRESH";
export const USERFURTHEREDUCATION_LIST_FAILURE = "USERFURTHEREDUCATION_LIST_FAILURE";
export const USERFURTHEREDUCATION_LIST_REMOVE = "USERFURTHEREDUCATION_LIST_REMOVE";
export const USERFURTHEREDUCATION_LIST_RESET = "USERFURTHEREDUCATION_LIST_RESET";
export const USERFURTHEREDUCATION_LIST_SETLASTOPENED = "USERFURTHEREDUCATION_LIST_SETLASTOPENED";

export const USERFURTHEREDUCATION_EDIT_REQUEST = "USERFURTHEREDUCATION_EDIT_REQUEST";
export const USERFURTHEREDUCATION_EDIT_SUCCESS = "USERFURTHEREDUCATION_EDIT_SUCCESS";
export const USERFURTHEREDUCATION_EDIT_FAILURE = "USERFURTHEREDUCATION_EDIT_FAILURE";
export const USERFURTHEREDUCATION_EDIT_RESET = "USERFURTHEREDUCATION_EDIT_RESET";
export const USERFURTHEREDUCATION_EDIT_SETRETURNURL = "USERFURTHEREDUCATION_EDIT_SETRETURNURL";

export type T_USERFURTHEREDUCATION_LIST_REQUEST = typeof USERFURTHEREDUCATION_LIST_REQUEST;
export type T_USERFURTHEREDUCATION_LIST_SUCCESS = typeof USERFURTHEREDUCATION_LIST_SUCCESS;
export type T_USERFURTHEREDUCATION_LIST_REFRESH = typeof USERFURTHEREDUCATION_LIST_REFRESH;
export type T_USERFURTHEREDUCATION_LIST_FAILURE = typeof USERFURTHEREDUCATION_LIST_FAILURE;
export type T_USERFURTHEREDUCATION_LIST_REMOVE = typeof USERFURTHEREDUCATION_LIST_REMOVE;
export type T_USERFURTHEREDUCATION_LIST_RESET = typeof USERFURTHEREDUCATION_LIST_RESET;
export type T_USERFURTHEREDUCATION_LIST_SETLASTOPENED = typeof USERFURTHEREDUCATION_LIST_SETLASTOPENED;

export type T_USERFURTHEREDUCATION_EDIT_REQUEST = typeof USERFURTHEREDUCATION_EDIT_REQUEST;
export type T_USERFURTHEREDUCATION_EDIT_SUCCESS = typeof USERFURTHEREDUCATION_EDIT_SUCCESS;
export type T_USERFURTHEREDUCATION_EDIT_FAILURE = typeof USERFURTHEREDUCATION_EDIT_FAILURE;
export type T_USERFURTHEREDUCATION_EDIT_RESET = typeof USERFURTHEREDUCATION_EDIT_RESET;
export type T_USERFURTHEREDUCATION_EDIT_SETRETURNURL = typeof USERFURTHEREDUCATION_EDIT_SETRETURNURL;

//InstantMessage
export const INSTANTMESSAGECHANNEL_LIST_REQUEST = "INSTANTMESSAGECHANNEL_LIST_REQUEST";
export const INSTANTMESSAGECHANNEL_LIST_SUCCESS = "INSTANTMESSAGECHANNEL_LIST_SUCCESS";
export const INSTANTMESSAGECHANNEL_LIST_FAILURE = "INSTANTMESSAGECHANNEL_LIST_FAILURE";
export const INSTANTMESSAGECHANNEL_LIST_REMOVE = "INSTANTMESSAGECHANNEL_LIST_REMOVE";
export const INSTANTMESSAGECHANNEL_LIST_RESET = "INSTANTMESSAGECHANNEL_LIST_RESET";
export const INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED = "INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED";

export const INSTANTMESSAGECHANNEL_EDIT_REQUEST = "INSTANTMESSAGECHANNEL_EDIT_REQUEST";
export const INSTANTMESSAGECHANNEL_EDIT_SUCCESS = "INSTANTMESSAGECHANNEL_EDIT_SUCCESS";
export const INSTANTMESSAGECHANNEL_EDIT_FAILURE = "INSTANTMESSAGECHANNEL_EDIT_FAILURE";
export const INSTANTMESSAGECHANNEL_EDIT_RESET = "INSTANTMESSAGECHANNEL_EDIT_RESET";
export const INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL = "INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL";

export const INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL = "INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL";

export type T_INSTANTMESSAGECHANNEL_LIST_REQUEST = typeof INSTANTMESSAGECHANNEL_LIST_REQUEST;
export type T_INSTANTMESSAGECHANNEL_LIST_SUCCESS = typeof INSTANTMESSAGECHANNEL_LIST_SUCCESS;
export type T_INSTANTMESSAGECHANNEL_LIST_FAILURE = typeof INSTANTMESSAGECHANNEL_LIST_FAILURE;
export type T_INSTANTMESSAGECHANNEL_LIST_REMOVE = typeof INSTANTMESSAGECHANNEL_LIST_REMOVE;
export type T_INSTANTMESSAGECHANNEL_LIST_RESET = typeof INSTANTMESSAGECHANNEL_LIST_RESET;
export type T_INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED = typeof INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED;

export type T_INSTANTMESSAGECHANNEL_EDIT_REQUEST = typeof INSTANTMESSAGECHANNEL_EDIT_REQUEST;
export type T_INSTANTMESSAGECHANNEL_EDIT_SUCCESS = typeof INSTANTMESSAGECHANNEL_EDIT_SUCCESS;
export type T_INSTANTMESSAGECHANNEL_EDIT_FAILURE = typeof INSTANTMESSAGECHANNEL_EDIT_FAILURE;
export type T_INSTANTMESSAGECHANNEL_EDIT_RESET = typeof INSTANTMESSAGECHANNEL_EDIT_RESET;
export type T_INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL = typeof INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL;

export type T_INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL = typeof INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL;

//CalendarTask
export const CALENDARTASK_VIEW_REQUEST = "CALENDARTASK_VIEW_REQUEST";
export const CALENDARTASK_VIEW_SUCCESS = "CALENDARTASK_VIEW_SUCCESS";
export const CALENDARTASK_VIEW_FAILURE = "CALENDARTASK_VIEW_FAILURE";
export const CALENDARTASK_VIEW_REMOVE = "CALENDARTASK_VIEW_REMOVE";
export const CALENDARTASK_VIEW_RESET = "CALENDARTASK_VIEW_RESET";
export const CALENDARTASK_VIEW_SETLASTOPENED = "CALENDARTASK_VIEW_SETLASTOPENED";
export const CALENDARTASK_VIEW_SETLOCKED = "CALENDARTASK_VIEW_SETLOCKED";

export const CALENDARTASK_EDIT_REQUEST = "CALENDARTASK_EDIT_REQUEST";
export const CALENDARTASK_EDIT_SUCCESS = "CALENDARTASK_EDIT_SUCCESS";
export const CALENDARTASK_EDIT_FAILURE = "CALENDARTASK_EDIT_FAILURE";
export const CALENDARTASK_EDIT_RESET = "CALENDARTASK_EDIT_RESET";
export const CALENDARTASK_EDIT_SETRETURNURL = "CALENDARTASK_EDIT_SETRETURNURL";

export const CALENDARTASK_OPEN_SETRETURNURL = "CALENDARTASK_OPEN_SETRETURNURL";

export type T_CALENDARTASK_VIEW_REQUEST = typeof CALENDARTASK_VIEW_REQUEST;
export type T_CALENDARTASK_VIEW_SUCCESS = typeof CALENDARTASK_VIEW_SUCCESS;
export type T_CALENDARTASK_VIEW_FAILURE = typeof CALENDARTASK_VIEW_FAILURE;
export type T_CALENDARTASK_VIEW_REMOVE = typeof CALENDARTASK_VIEW_REMOVE;
export type T_CALENDARTASK_VIEW_RESET = typeof CALENDARTASK_VIEW_RESET;
export type T_CALENDARTASK_VIEW_SETLASTOPENED = typeof CALENDARTASK_VIEW_SETLASTOPENED;
export type T_CALENDARTASK_VIEW_SETLOCKED = typeof CALENDARTASK_VIEW_SETLOCKED;

export type T_CALENDARTASK_EDIT_REQUEST = typeof CALENDARTASK_EDIT_REQUEST;
export type T_CALENDARTASK_EDIT_SUCCESS = typeof CALENDARTASK_EDIT_SUCCESS;
export type T_CALENDARTASK_EDIT_FAILURE = typeof CALENDARTASK_EDIT_FAILURE;
export type T_CALENDARTASK_EDIT_RESET = typeof CALENDARTASK_EDIT_RESET;
export type T_CALENDARTASK_EDIT_SETRETURNURL = typeof CALENDARTASK_EDIT_SETRETURNURL;

//IdCard
export const IDCARD_VIEW_REQUEST = "IDCARD_VIEW_REQUEST";
export const IDCARD_VIEW_SUCCESS = "IDCARD_VIEW_SUCCESS";
export const IDCARD_VIEW_FAILURE = "IDCARD_VIEW_FAILURE";

export type T_IDCARD_VIEW_REQUEST = typeof IDCARD_VIEW_REQUEST;
export type T_IDCARD_VIEW_SUCCESS = typeof IDCARD_VIEW_SUCCESS;
export type T_IDCARD_VIEW_FAILURE = typeof IDCARD_VIEW_FAILURE;

//Action types

//Install
export interface IInstallSet extends Action {
    type: T_INSTALL_SET;
    event: IBeforeInstallPromptEvent;
}

export interface IInstallClear extends Action {
    type: T_INSTALL_CLEAR;
}

export type InstallAction =
    | IInstallSet
    | IInstallClear;

//Update
export interface IUpdateSet extends Action {
    type: T_UPDATE_SET;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
}

export interface IUpdateClear extends Action {
    type: T_UPDATE_CLEAR;
}

export type UpdateAction =
    | IUpdateSet
    | IUpdateClear;

//Alert
export interface IAlertSuccess extends Action {
    type: T_ALERT_SUCCESS;
    message: string;
}

export interface IAlertInfo extends Action {
    type: T_ALERT_INFO;
    message: string;
}

export interface IAlertError extends Action {
    type: T_ALERT_ERROR;
    message: string;
}

export interface IAlertClear extends Action {
    type: T_ALERT_CLEAR;
}

export interface IAlertSuccessClear extends Action {
    type: T_ALERT_SUCCESS_CLEAR;
}

export interface IAlertInfoClear extends Action {
    type: T_ALERT_INFO_CLEAR;
}

export type AlertAction =
    | IAlertSuccess
    | IAlertInfo
    | IAlertError
    | IAlertClear
    | IAlertSuccessClear
    | IAlertInfoClear;

//Confirmation
export interface IConfirmationSet extends Action {
    type: T_CONFIRMATION_SET;
    dialogType: ConfirmationDialogType;
    title: string;
    text: string;
    show: boolean;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
    onCancel: IConfirmationCallback;
}

export interface IConfirmationClear extends Action {
    type: T_CONFIRMATION_CLEAR;
}

export type ConfirmationAction =
    | IConfirmationSet
    | IConfirmationClear;

//Fetch
export interface IFetchStart extends Action {
    type: T_FETCH_START;
}

export interface IFetchEnd extends Action {
    type: T_FETCH_END;
}

export type FetchAction =
    | IFetchStart
    | IFetchEnd;

//Login
export interface ILoginSuccess extends Action {
    type: T_LOGIN_SUCCESS;
    companyId: string;
    companyName: string;
    userCode: string;
    userName: string;
    userPicture: string;
    canUseMessages: boolean;
    canUseDocuments: boolean;
    canUseEvents: boolean;
    canUseTasks: boolean;
    canUseInstantMessages: boolean;
    canEditOwnRecord: boolean;
    canUseCalendarTasks: boolean;
    canUseMultipleCompanies: boolean;
}

export interface ILoginFailure extends Action {
    type: T_LOGIN_FAILURE;
}

export interface ILogoutSuccess extends Action {
    type: T_LOGOUT_SUCCESS;
}

export type LoginAction =
    | ILoginSuccess
    | ILoginFailure
    | ILogoutSuccess;

//Message
export interface IMessageListRequest extends Action {
    type: T_MESSAGE_LIST_REQUEST;
}

export interface IMessageListSuccess extends Action {
    type: T_MESSAGE_LIST_SUCCESS;
    messageList: IMessageList;
    startDate: number;
    endDate: number;
    companyId: string;
}

export interface IMessageListRefresh extends Action {
    type: T_MESSAGE_LIST_REFRESH;
    messageList: IMessageList;
    startDate: number;
    endDate: number;
    companyId: string;
}

export interface IMessageListFailure extends Action {
    type: T_MESSAGE_LIST_FAILURE;
}

export interface IMessageListSetLastOpened extends Action {
    type: T_MESSAGE_LIST_SETLASTOPENED;
    messageId: string;
}

export interface IMessageListReset extends Action {
    type: T_MESSAGE_LIST_RESET;
}

export interface IMessageEditRequest extends Action {
    type: T_MESSAGE_EDIT_REQUEST;
}

export interface IMessageEditSuccess extends Action {
    type: T_MESSAGE_EDIT_SUCCESS;
    messageEdit: IMessageEdit;
}

export interface IMessageEditFailure extends Action {
    type: T_MESSAGE_EDIT_FAILURE;
}

export interface IMessageEditReset extends Action {
    type: T_MESSAGE_EDIT_RESET;
}

export interface IMessageEditSetReturnUrl extends Action {
    type: T_MESSAGE_EDIT_SETRETURNURL;
    returnUrl: string;
}

export type MessageAction =
    | IMessageListRequest
    | IMessageListSuccess
    | IMessageListRefresh
    | IMessageListFailure
    | IMessageListSetLastOpened
    | IMessageListReset
    | IMessageEditRequest
    | IMessageEditSuccess
    | IMessageEditFailure
    | IMessageEditReset
    | IMessageEditSetReturnUrl;

//Event
export interface IEventListRequest extends Action {
    type: T_EVENT_LIST_REQUEST;
}

export interface IEventListSuccess extends Action {
    type: T_EVENT_LIST_SUCCESS;
    eventList: IEventList;
    state: number;
    startDate: number;
    endDate: number;
    unitId: string;
}

export interface IEventListRefresh extends Action {
    type: T_EVENT_LIST_REFRESH;
    eventList: IEventList;
    state: number;
    startDate: number;
    endDate: number;
    unitId: string;
}

export interface IEventListFailure extends Action {
    type: T_EVENT_LIST_FAILURE;
}

export interface IEventListRemove extends Action {
    type: T_EVENT_LIST_REMOVE;
    eventId: string;
}

export interface IEventListReset extends Action {
    type: T_EVENT_LIST_RESET;
}

export interface IEventListSetLastOpened extends Action {
    type: T_EVENT_LIST_SETLASTOPENED;
    eventId: string;
}

export interface IEventListSetLocked extends Action {
    type: T_EVENT_LIST_SETLOCKED;
    eventId: string;
    locked: boolean;
}

export interface IEventEditRequest extends Action {
    type: T_EVENT_EDIT_REQUEST;
}

export interface IEventEditSuccess extends Action {
    type: T_EVENT_EDIT_SUCCESS;
    eventEdit: IEventEdit;
}

export interface IEventEditFailure extends Action {
    type: T_EVENT_EDIT_FAILURE;
}

export interface IEventEditReset extends Action {
    type: T_EVENT_EDIT_RESET;
}

export interface IEventEditSetReturnUrl extends Action {
    type: T_EVENT_EDIT_SETRETURNURL;
    returnUrl: string;
}

export type EventAction =
    | IEventListRequest
    | IEventListSuccess
    | IEventListRefresh
    | IEventListFailure
    | IEventListRemove
    | IEventListReset
    | IEventListSetLastOpened
    | IEventListSetLocked
    | IEventEditRequest
    | IEventEditSuccess
    | IEventEditFailure
    | IEventEditReset
    | IEventEditSetReturnUrl;

//Task
export interface ITaskListRequest extends Action {
    type: T_TASK_LIST_REQUEST;
}

export interface ITaskListSuccess extends Action {
    type: T_TASK_LIST_SUCCESS;
    taskList: ITaskList;
    stateId: string;
    unitId: string;
}

export interface ITaskListRefresh extends Action {
    type: T_TASK_LIST_REFRESH;
    taskList: ITaskList;
    stateId: string;
    unitId: string;
}

export interface ITaskListFailure extends Action {
    type: T_TASK_LIST_FAILURE;
}

export interface ITaskListRemove extends Action {
    type: T_TASK_LIST_REMOVE;
    taskId: string;
}

export interface ITaskListReset extends Action {
    type: T_TASK_LIST_RESET;
}

export interface ITaskListSetLastOpened extends Action {
    type: T_TASK_LIST_SETLASTOPENED;
    taskId: string;
}

export interface ITaskListSetLocked extends Action {
    type: T_TASK_LIST_SETLOCKED;
    taskId: string;
    locked: boolean;
}

export interface ITaskEditRequest extends Action {
    type: T_TASK_EDIT_REQUEST;
}

export interface ITaskEditSuccess extends Action {
    type: T_TASK_EDIT_SUCCESS;
    taskEdit: ITaskEdit;
}

export interface ITaskEditFailure extends Action {
    type: T_TASK_EDIT_FAILURE;
}

export interface ITaskEditReset extends Action {
    type: T_TASK_EDIT_RESET;
}

export interface ITaskEditSetReturnUrl extends Action {
    type: T_TASK_EDIT_SETRETURNURL;
    returnUrl: string;
}

export type TaskAction =
    | ITaskListRequest
    | ITaskListSuccess
    | ITaskListRefresh
    | ITaskListFailure
    | ITaskListRemove
    | ITaskListReset
    | ITaskListSetLastOpened
    | ITaskListSetLocked
    | ITaskEditRequest
    | ITaskEditSuccess
    | ITaskEditFailure
    | ITaskEditReset
    | ITaskEditSetReturnUrl;

//Document
export interface IDocumentListRequest extends Action {
    type: T_DOCUMENT_LIST_REQUEST;
}

export interface IDocumentListSuccess extends Action {
    type: T_DOCUMENT_LIST_SUCCESS;
    documentList: IDocumentList;
    filter: string;
}

export interface IDocumentListFailure extends Action {
    type: T_DOCUMENT_LIST_FAILURE;
}

export interface IDocumentListSetLastOpened extends Action {
    type: T_DOCUMENT_LIST_SETLASTOPENED;
    documentId: string;
}

export interface IDocumentListReset extends Action {
    type: T_DOCUMENT_LIST_RESET;
}

export interface IDocumentListAddFavorite extends Action {
    type: T_DOCUMENT_LIST_ADD_FAVORITE;
    folderId: string;
}

export interface IDocumentListRemoveFavorite extends Action {
    type: T_DOCUMENT_LIST_REMOVE_FAVORITE;
    folderId: string;
}

export interface IDocumentViewRequest extends Action {
    type: T_DOCUMENT_VIEW_REQUEST;
}

export interface IDocumentViewSuccess extends Action {
    type: T_DOCUMENT_VIEW_SUCCESS;
    documentView: IDocumentView;
}

export interface IDocumentViewFailure extends Action {
    type: T_DOCUMENT_VIEW_FAILURE;
}

export interface IDocumentViewReset extends Action {
    type: T_DOCUMENT_VIEW_RESET;
}

export interface IDocumentViewSetReturnUrl extends Action {
    type: T_DOCUMENT_VIEW_SETRETURNURL;
    returnUrl: string;
}

export type DocumentAction =
    | IDocumentListRequest
    | IDocumentListSuccess
    | IDocumentListFailure
    | IDocumentListSetLastOpened
    | IDocumentListReset
    | IDocumentListAddFavorite
    | IDocumentListRemoveFavorite
    | IDocumentViewRequest
    | IDocumentViewSuccess
    | IDocumentViewFailure
    | IDocumentViewReset
    | IDocumentViewSetReturnUrl
    ;

//UserFurtherEducation
export interface IUserFurtherEducationListRequest extends Action {
    type: T_USERFURTHEREDUCATION_LIST_REQUEST;
}

export interface IUserFurtherEducationListSuccess extends Action {
    type: T_USERFURTHEREDUCATION_LIST_SUCCESS;
    list: IUserFurtherEducationList;
    archived: boolean;
}

export interface IUserFurtherEducationListRefresh extends Action {
    type: T_USERFURTHEREDUCATION_LIST_REFRESH;
    list: IUserFurtherEducationList;
    archived: boolean;
}

export interface IUserFurtherEducationListFailure extends Action {
    type: T_USERFURTHEREDUCATION_LIST_FAILURE;
}

export interface IUserFurtherEducationListRemove extends Action {
    type: T_USERFURTHEREDUCATION_LIST_REMOVE;
    userFurtherEducationId: string;
}

export interface IUserFurtherEducationListReset extends Action {
    type: T_USERFURTHEREDUCATION_LIST_RESET;
}

export interface IUserFurtherEducationListSetLastOpened extends Action {
    type: T_USERFURTHEREDUCATION_LIST_SETLASTOPENED;
    userFurtherEducationId: string;
}

export interface IUserFurtherEducationEditRequest extends Action {
    type: T_USERFURTHEREDUCATION_EDIT_REQUEST;
}

export interface IUserFurtherEducationEditSuccess extends Action {
    type: T_USERFURTHEREDUCATION_EDIT_SUCCESS;
    userFurtherEducationEdit: IUserFurtherEducationEdit;
}

export interface IUserFurtherEducationEditFailure extends Action {
    type: T_USERFURTHEREDUCATION_EDIT_FAILURE;
}

export interface IUserFurtherEducationEditReset extends Action {
    type: T_USERFURTHEREDUCATION_EDIT_RESET;
}

export interface IUserFurtherEducationEditSetReturnUrl extends Action {
    type: T_USERFURTHEREDUCATION_EDIT_SETRETURNURL;
    returnUrl: string;
}

export type UserFurtherEducationAction =
    | IUserFurtherEducationListRequest
    | IUserFurtherEducationListSuccess
    | IUserFurtherEducationListRefresh
    | IUserFurtherEducationListFailure
    | IUserFurtherEducationListRemove
    | IUserFurtherEducationListReset
    | IUserFurtherEducationListSetLastOpened
    | IUserFurtherEducationEditRequest
    | IUserFurtherEducationEditSuccess
    | IUserFurtherEducationEditFailure
    | IUserFurtherEducationEditReset
    | IUserFurtherEducationEditSetReturnUrl;

//InstantMessage

//InstantMessageChannelList
export interface IInstantMessageChannelListRequest extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_REQUEST;
}

export interface IInstantMessageChannelListSuccess extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_SUCCESS;
    list: IInstantMessageChannelItems;
}

export interface IInstantMessageChannelListFailure extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_FAILURE;
}

export interface IInstantMessageChannelListRemove extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_REMOVE;
    instantMessageChannelId: string;
}

export interface IInstantMessageChannelListReset extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_RESET;
}

export interface IInstantMessageChannelListSetLastOpened extends Action {
    type: T_INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED;
    instantMessageChannelId: string;
}

//InstantMessageChannelEdit
export interface IInstantMessageChannelEditRequest extends Action {
    type: T_INSTANTMESSAGECHANNEL_EDIT_REQUEST;
}

export interface IInstantMessageChannelEditSuccess extends Action {
    type: T_INSTANTMESSAGECHANNEL_EDIT_SUCCESS;
    item: IInstantMessageChannelEdit;
}

export interface IInstantMessageChannelEditFailure extends Action {
    type: T_INSTANTMESSAGECHANNEL_EDIT_FAILURE;
}

export interface IInstantMessageChannelEditReset extends Action {
    type: T_INSTANTMESSAGECHANNEL_EDIT_RESET;
}

export interface IInstantMessageChannelEditSetReturnUrl extends Action {
    type: T_INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL;
    returnUrl: string;
}

//InstantMessageChannelOpen
export interface IInstantMessageChannelOpenSetReturnUrl extends Action {
    type: T_INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL;
    returnUrl: string;
}

export type InstantMessageAction =
    | IInstantMessageChannelListRequest
    | IInstantMessageChannelListSuccess
    | IInstantMessageChannelListFailure
    | IInstantMessageChannelListRemove
    | IInstantMessageChannelListReset
    | IInstantMessageChannelListSetLastOpened
    | IInstantMessageChannelEditRequest
    | IInstantMessageChannelEditSuccess
    | IInstantMessageChannelEditFailure
    | IInstantMessageChannelEditReset
    | IInstantMessageChannelEditSetReturnUrl
    | IInstantMessageChannelOpenSetReturnUrl;

//CalendarTask

//CalendarTaskView
export interface ICalendarTaskViewRequest extends Action {
    type: T_CALENDARTASK_VIEW_REQUEST;
}

export interface ICalendarTaskViewSuccess extends Action {
    type: T_CALENDARTASK_VIEW_SUCCESS;
    filter: string;
    stateId: string;
    categoryId: string;
    items: IMobileCalendarTaskItems;
}

export interface ICalendarTaskViewFailure extends Action {
    type: T_CALENDARTASK_VIEW_FAILURE;
}

export interface ICalendarTaskViewRemove extends Action {
    type: T_CALENDARTASK_VIEW_REMOVE;
    calendarTaskId: string;
}

export interface ICalendarTaskViewReset extends Action {
    type: T_CALENDARTASK_VIEW_RESET;
}

export interface ICalendarTaskViewSetLastOpened extends Action {
    type: T_CALENDARTASK_VIEW_SETLASTOPENED;
    calendarTaskId: string;
}

export interface ICalendarTaskViewSetLocked extends Action {
    type: T_CALENDARTASK_VIEW_SETLOCKED;
    calendarTaskId: string;
    locked: boolean;
}

//CalendarTaskEdit
export interface ICalendarTaskEditRequest extends Action {
    type: T_CALENDARTASK_EDIT_REQUEST;
}

export interface ICalendarTaskEditSuccess extends Action {
    type: T_CALENDARTASK_EDIT_SUCCESS;
    item: ICalendarTaskEdit;
}

export interface ICalendarTaskEditFailure extends Action {
    type: T_CALENDARTASK_EDIT_FAILURE;
}

export interface ICalendarTaskEditReset extends Action {
    type: T_CALENDARTASK_EDIT_RESET;
}

export interface ICalendarTaskEditSetReturnUrl extends Action {
    type: T_CALENDARTASK_EDIT_SETRETURNURL;
    returnUrl: string;
}

export type CalendarTaskAction =
    | ICalendarTaskViewRequest
    | ICalendarTaskViewSuccess
    | ICalendarTaskViewFailure
    | ICalendarTaskViewRemove
    | ICalendarTaskViewReset
    | ICalendarTaskViewSetLastOpened
    | ICalendarTaskViewSetLocked
    | ICalendarTaskEditRequest
    | ICalendarTaskEditSuccess
    | ICalendarTaskEditFailure
    | ICalendarTaskEditReset
    | ICalendarTaskEditSetReturnUrl;

//IdCardView
export interface IIdCardViewRequest extends Action {
    type: T_IDCARD_VIEW_REQUEST;
}

export interface IIdCardViewSuccess extends Action {
    type: T_IDCARD_VIEW_SUCCESS;
    item: IUserIdCardItem;
}

export interface IIdCardViewFailure extends Action {
    type: T_IDCARD_VIEW_FAILURE;
}

export type IdCardViewAction =
    | IIdCardViewRequest
    | IIdCardViewSuccess
    | IIdCardViewFailure;

//FrontPage
export interface IFrontPageRequest extends Action {
    type: T_FRONT_PAGE_REQUEST;
}

export interface IFrontPageSuccess extends Action {
    type: T_FRONT_PAGE_SUCCESS;
    frontPage: IFrontPage;
}

export interface IFrontPageFailure extends Action {
    type: T_FRONT_PAGE_FAILURE;
}

//FrontPage: Message
export interface IFrontPageMessageRequest extends Action {
    type: T_FRONT_PAGE_MESSAGE_REQUEST;
}

export interface IFrontPageMessageSuccess extends Action {
    type: T_FRONT_PAGE_MESSAGE_SUCCESS;
    frontPageMessage: IFrontPageMessage;
}

export interface IFrontPageMessageFailure extends Action {
    type: T_FRONT_PAGE_MESSAGE_FAILURE;
}

//FrontPage: Event
export interface IFrontPageEventRequest extends Action {
    type: T_FRONT_PAGE_EVENT_REQUEST;
}

export interface IFrontPageEventSuccess extends Action {
    type: T_FRONT_PAGE_EVENT_SUCCESS;
    frontPageEvent: IFrontPageEvent;
}

export interface IFrontPageEventFailure extends Action {
    type: T_FRONT_PAGE_EVENT_FAILURE;
}

//FrontPage: Task
export interface IFrontPageTaskRequest extends Action {
    type: T_FRONT_PAGE_TASK_REQUEST;
}

export interface IFrontPageTaskSuccess extends Action {
    type: T_FRONT_PAGE_TASK_SUCCESS;
    frontPageTask: IFrontPageTask;
}

export interface IFrontPageTaskFailure extends Action {
    type: T_FRONT_PAGE_TASK_FAILURE;
}

//FrontPage: Document
export interface IFrontPageDocumentRequest extends Action {
    type: T_FRONT_PAGE_DOCUMENT_REQUEST;
}

export interface IFrontPageDocumentSuccess extends Action {
    type: T_FRONT_PAGE_DOCUMENT_SUCCESS;
    frontPageDocument: IFrontPageDocument;
}

export interface IFrontPageDocumentFailure extends Action {
    type: T_FRONT_PAGE_DOCUMENT_FAILURE;
}

//FrontPage: InstantMessage
export interface IFrontPageInstantMessageRequest extends Action {
    type: T_FRONT_PAGE_INSTANTMESSAGE_REQUEST;
}

export interface IFrontPageInstantMessageSuccess extends Action {
    type: T_FRONT_PAGE_INSTANTMESSAGE_SUCCESS;
    frontPageInstantMessage: IFrontPageInstantMessage;
}

export interface IFrontPageInstantMessageFailure extends Action {
    type: T_FRONT_PAGE_INSTANTMESSAGE_FAILURE;
}

//FrontPage: CalendarTask
export interface IFrontPageCalendarTaskRequest extends Action {
    type: T_FRONT_PAGE_CALENDARTASK_REQUEST;
}

export interface IFrontPageCalendarTaskSuccess extends Action {
    type: T_FRONT_PAGE_CALENDARTASK_SUCCESS;
    frontPageCalendarTask: IFrontPageCalendarTask;
}

export interface IFrontPageCalendarTaskFailure extends Action {
    type: T_FRONT_PAGE_CALENDARTASK_FAILURE;
}

export type FrontAction =
    | IFrontPageRequest
    | IFrontPageSuccess
    | IFrontPageFailure
    //Message
    | IFrontPageMessageRequest
    | IFrontPageMessageSuccess
    | IFrontPageMessageFailure
    | IMessageListSetLastOpened
    | IMessageEditSetReturnUrl
    //Event
    | IFrontPageEventRequest
    | IFrontPageEventSuccess
    | IFrontPageEventFailure
    | IEventListSetLastOpened
    | IEventEditSetReturnUrl
    //Task
    | IFrontPageTaskRequest
    | IFrontPageTaskSuccess
    | IFrontPageTaskFailure
    | ITaskListSetLastOpened
    | ITaskEditSetReturnUrl
    //Document
    | IFrontPageDocumentRequest
    | IFrontPageDocumentSuccess
    | IFrontPageDocumentFailure
    | IDocumentListSetLastOpened
    //Instant message
    | IFrontPageInstantMessageRequest
    | IFrontPageInstantMessageSuccess
    | IFrontPageInstantMessageFailure
    | IInstantMessageChannelListSetLastOpened
    | IInstantMessageChannelOpenSetReturnUrl
    //CalendarTask
    | IFrontPageCalendarTaskRequest
    | IFrontPageCalendarTaskSuccess
    | IFrontPageCalendarTaskFailure
    | ICalendarTaskViewSetLastOpened
    | ICalendarTaskEditSetReturnUrl;
